import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Skeleton,
  Fade,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Alert } from '@material-ui/lab';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import {
  AttachMoney as AttachMoneyIcon,
  EventAvailable as EventAvailableIcon,
  Group as GroupIcon,
  Sports as SportsIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as VisibilityIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  MonetizationOn as MoneyIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  Share as ShareIcon
} from '@material-ui/icons';
import admin_api from '../../../api/admin';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
import ExportDialog from '../admin/export/ExportDialog';

// QR Code Display Component
const QRCodeDisplay = ({ qrImagePath, size = 250 }) => {
  return (
    <div
      style={{
        width: size,
        height: size,
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        boxSizing: 'border-box',
        position: 'relative'
      }}
    >
      {/* You can replace this with your actual QR code image */}
      <img 
        src={qrImagePath || "/static/rentacourt_user.png"} 
        alt="Court Booking App QR Code"
        style={{
          width: '150%',
          height: '150%',
          objectFit: 'contain'
        }}
        onError={(e) => {
          // Fallback to placeholder if image fails to load
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'block';
        }}
      />
      
      {/* Fallback placeholder */}
      <div style={{
        width: '80%',
        height: '80%',
        backgroundColor: '#003875',
        borderRadius: '4px',
        position: 'relative',
        display: 'none', // Hidden by default, shown if image fails
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '14px',
        textAlign: 'center'
      }}>
        QR Code<br />Placeholder
      </div>
      
      {/* App Label */}
      <Typography variant="caption" style={{
        position: 'absolute',
        bottom: '8px',
        color: '#64748b',
        fontWeight: '500',
        fontSize: '10px'
      }}>
        Court Booking App
      </Typography>
    </div>
  );
};

// Styles using makeStyles for MUI v4
const useStyles = makeStyles((theme) => ({
  styledContainer: {
    backgroundColor: '#12192c',
    minHeight: 'calc(100vh - 100px)',
    padding: '20px',
    color: '#ffffff',
  },
  styledPaper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
    border: '1px solid #2d3748',
  },
  styledCard: {
    backgroundColor: '#1e293b',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
    border: '1px solid #2d3748',
    height: '100%',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
    },
  },
  headerCard: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: 'white',
    borderRadius: theme.spacing(2),
    marginBottom: theme.spacing(4),
    boxShadow: '0 10px 30px rgba(6, 182, 212, 0.3)',
  },
  metricCard: {
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    height: '100%',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
    },
  },
  metricCardGreen: {
    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.25) 100%)',
    border: '1px solid rgba(34, 197, 94, 0.4)',
    boxShadow: '0 4px 20px rgba(34, 197, 94, 0.2)',
    '&:hover': {
      boxShadow: '0 8px 30px rgba(34, 197, 94, 0.2)',
      border: '1px solid rgba(34, 197, 94, 0.6)',
    },
  },
  metricCardBlue: {
    background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.15) 0%, rgba(6, 182, 212, 0.25) 100%)',
    border: '1px solid rgba(6, 182, 212, 0.4)',
    boxShadow: '0 4px 20px rgba(6, 182, 212, 0.2)',
    '&:hover': {
      boxShadow: '0 8px 30px rgba(6, 182, 212, 0.2)',
      border: '1px solid rgba(6, 182, 212, 0.6)',
    },
  },
  metricCardPurple: {
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.25) 100%)',
    border: '1px solid rgba(139, 92, 246, 0.4)',
    boxShadow: '0 4px 20px rgba(139, 92, 246, 0.2)',
    '&:hover': {
      boxShadow: '0 8px 30px rgba(139, 92, 246, 0.2)',
      border: '1px solid rgba(139, 92, 246, 0.6)',
    },
  },
  metricCardOrange: {
    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(245, 158, 11, 0.25) 100%)',
    border: '1px solid rgba(245, 158, 11, 0.4)',
    boxShadow: '0 4px 20px rgba(245, 158, 11, 0.2)',
    '&:hover': {
      boxShadow: '0 8px 30px rgba(245, 158, 11, 0.2)',
      border: '1px solid rgba(245, 158, 11, 0.6)',
    },
  },
  styledButton: {
    background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: 'white',
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 500,
    boxShadow: '0 4px 15px rgba(6, 182, 212, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)',
      boxShadow: '0 6px 20px rgba(6, 182, 212, 0.4)',
      transform: 'translateY(-2px)',
    },
    '&:disabled': {
      background: theme.palette.grey[700],
      boxShadow: 'none',
    },
  },
  tableHead: {
    '& .MuiTableCell-head': {
      backgroundColor: '#1e293b',
      color: '#06b6d4',
      fontWeight: 'bold',
      borderBottom: '2px solid #2d3748'
    }
  },
  tableRow: {
    '& .MuiTableCell-root': {
      color: '#e2e8f0',
      borderColor: '#2d3748'
    },
    '&:hover': {
      backgroundColor: 'rgba(6, 182, 212, 0.05)'
    }
  },
  dialogPaper: {
    backgroundColor: '#1a2235',
    color: '#ffffff',
    borderRadius: theme.spacing(2),
    border: '1px solid #2d3748',
  },
  skeletonDark: {
    backgroundColor: '#2d3748',
  }
}));

export default function MainDashboard() {
  const classes = useStyles();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [openFinancialModal, setOpenFinancialModal] = useState(false);
  const [openQRModal, setOpenQRModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const { isCollapse } = useCollapseDrawer();
  
  const DRAWER_WIDTH = 280;
  const COLLAPSE_WIDTH = 102;

  const containerStyle = {
    marginTop: '80px',
    marginLeft: isCollapse ? `${COLLAPSE_WIDTH + 24}px` : `${DRAWER_WIDTH + 24}px`,
    marginRight: '24px',
    transition: 'all 0.3s ease',
  };

  if (window.innerWidth <= 1024) {
    containerStyle.marginLeft = '24px';
  }

  function formatDate(dateString) {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'short',
      day: '2-digit'
    }).format(date);
  }
  
  function convertTo12Hour(timeString) {
    if (!timeString) return '';
    
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    const [hours, minutes] = timeString.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) {
      return timeString;
    }
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await admin_api.get_transactions();
      
      let transactionsData = [];
      
      if (response && response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          transactionsData = response.data.data;
        } else if (response.data.transactions && Array.isArray(response.data.transactions)) {
          transactionsData = response.data.transactions;
        } else if (Array.isArray(response.data)) {
          transactionsData = response.data;
        } else if (response.data.success && response.data.data && Array.isArray(response.data.data)) {
          transactionsData = response.data.data;
        } else {
          console.warn('Unexpected API response structure:', response.data);
          transactionsData = [];
        }
      } else {
        console.warn('Empty or invalid API response');
        transactionsData = [];
      }
      
      setTransactions(transactionsData);
      setSuccessMessage('Dashboard data loaded successfully');
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Failed to fetch dashboard data. Please try again.');
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Safe accessor function for transaction properties - IMPROVED
  const getProperty = (obj, propNames) => {
    if (!obj) return '';
    for (const prop of propNames) {
      if (obj[prop] !== undefined && obj[prop] !== null && obj[prop] !== '') {
        return obj[prop];
      }
    }
    return '';
  };

  // Calculate dashboard metrics - FIXED REVENUE CALCULATION
  const dashboardMetrics = useMemo(() => {
    if (!transactions || transactions.length === 0) {
      return {
        totalRevenue: '0.00',
        todayBookings: 0,
        uniqueCustomers: 0,
        uniqueCourts: 0,
        weeklyGrowth: 0,
        avgBookingValue: '0.00'
      };
    }

    try {
      // FIXED: Proper revenue calculation with better property handling
      const totalRevenue = transactions.reduce((sum, transaction) => {
        // Try multiple possible property names for amount
        let amount = 0;
        
        // Check various property names that might contain the amount
        const possibleAmountFields = [
          'totalAmount', 'total_amount', 
          'amountToPay', 'amount_to_pay', 
          'baseAmount', 'base_amount',
          'amount', 'total', 'price'
        ];
        
        for (const field of possibleAmountFields) {
          if (transaction[field] !== undefined && transaction[field] !== null) {
            const value = parseFloat(transaction[field]);
            if (!isNaN(value) && value > 0) {
              amount = value;
              break;
            }
          }
        }
        
        return sum + amount;
      }, 0);
      
      // Better customer counting
      const uniqueCustomers = new Set(
        transactions
          .map(t => getProperty(t, ['userId', 'user_id', 'customerName', 'customer_name', 'userName', 'user_name']))
          .filter(name => name && String(name).trim() !== '')
      ).size;
      
      // Better court counting  
      const uniqueCourts = new Set(
        transactions
          .map(t => getProperty(t, ['courtId', 'court_id', 'courtName', 'court_name']))
          .filter(court => court && String(court).trim() !== '')
      ).size;
      
      // Get today's bookings
      const todayPH = new Date().toLocaleString('en-US', {timeZone: 'Asia/Manila'});
      const today = new Date(todayPH);
      
      const todayBookings = transactions.filter(t => {
        const dateStr = getProperty(t, ['reservationDate', 'reservation_date', 'date', 'booking_date']);
        if (!dateStr) return false;
        
        try {
          const date = new Date(dateStr);
          const datePH = new Date(date.toLocaleString('en-US', {timeZone: 'Asia/Manila'}));
          
          return datePH.getDate() === today.getDate() && 
                 datePH.getMonth() === today.getMonth() && 
                 datePH.getFullYear() === today.getFullYear();
        } catch (error) {
          return false;
        }
      }).length;

      // Calculate weekly growth
      const thisWeekStart = new Date(today);
      thisWeekStart.setDate(today.getDate() - 7);
      
      const thisWeekBookings = transactions.filter(t => {
        const dateStr = getProperty(t, ['reservationDate', 'reservation_date', 'date', 'booking_date']);
        if (!dateStr) return false;
        
        try {
          const date = new Date(dateStr);
          return date >= thisWeekStart && date <= today;
        } catch (error) {
          return false;
        }
      }).length;
      
      const lastWeekStart = new Date(thisWeekStart);
      lastWeekStart.setDate(thisWeekStart.getDate() - 7);
      
      const lastWeekBookings = transactions.filter(t => {
        const dateStr = getProperty(t, ['reservationDate', 'reservation_date', 'date', 'booking_date']);
        if (!dateStr) return false;
        
        try {
          const date = new Date(dateStr);
          return date >= lastWeekStart && date < thisWeekStart;
        } catch (error) {
          return false;
        }
      }).length;
      
      const weeklyGrowth = lastWeekBookings > 0 
        ? ((thisWeekBookings - lastWeekBookings) / lastWeekBookings * 100)
        : thisWeekBookings > 0 ? 100 : 0;

      return {
        totalRevenue: totalRevenue.toFixed(2),
        todayBookings,
        uniqueCustomers,
        uniqueCourts,
        weeklyGrowth: weeklyGrowth.toFixed(1),
        avgBookingValue: transactions.length > 0 ? (totalRevenue / transactions.length).toFixed(2) : '0.00'
      };
    } catch (error) {
      console.error('Error calculating dashboard metrics:', error);
      return {
        totalRevenue: '0.00',
        todayBookings: 0,
        uniqueCustomers: 0,
        uniqueCourts: 0,
        weeklyGrowth: 0,
        avgBookingValue: '0.00'
      };
    }
  }, [transactions]);

  const handleViewFinancialDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setOpenFinancialModal(true);
  };

  // QR CODE HANDLERS - NEW
  const handleOpenQRModal = () => {
    setOpenQRModal(true);
  };

  const handlePrintQR = () => {
    window.print();
  };

  const generateAppURL = () => {
    return 'https://courtbooking.app/download';
  };

  const generateShareText = () => {
    return `🏆 Book courts easily with our Court Booking App! 
📱 Download now: ${generateAppURL()}
🎾 Available courts, instant booking, secure payments
#CourtBooking #Sports #BookingApp`;
  };

  const handleShareQR = async () => {
    const shareText = generateShareText();
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Court Booking App',
          text: shareText,
          url: generateAppURL(),
        });
      } catch (error) {
        navigator.clipboard.writeText(shareText);
        setSuccessMessage('Sharing text copied to clipboard!');
      }
    } else {
      navigator.clipboard.writeText(shareText);
      setSuccessMessage('Sharing text copied to clipboard!');
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
        setError(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage, error]);

  return (
    <Box className={classes.styledContainer} style={containerStyle}>
      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" style={{ backgroundColor: '#22c55e' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" style={{ backgroundColor: '#ef4444' }}>
          {error}
        </Alert>
      </Snackbar>

      <Fade in timeout={800}>
        <Box>
          {/* Header */}
          <Card className={classes.headerCard}>
            <CardContent style={{ padding: '32px' }}>
              <Grid container alignItems="center" justify="space-between">
                <Grid item>
                  <Typography variant="h3" style={{ fontWeight: 700, marginBottom: '8px' }}>
                    Dashboard Overview
                  </Typography>
                  <Typography variant="h6" style={{ opacity: 0.9 }}>
                    Monitor your court booking business performance
                  </Typography>
                </Grid>
                <Grid item>
                  <Box style={{ display: 'flex', gap: '16px' }}>
                    {/* QR CODE BUTTON */}
                    <Tooltip title="Generate QR Code for App Promotion">
                      <Button
                        className={classes.styledButton}
                        startIcon={<QrCodeIcon />}
                        onClick={handleOpenQRModal}
                        style={{
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        }}
                      >
                        QR Code
                      </Button>
                    </Tooltip>
                    <IconButton
                      onClick={fetchTransactions}
                      disabled={loading}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                      }}
                    >
                      <RefreshIcon />
                    </IconButton>
                    <Button
                      className={classes.styledButton}
                      startIcon={<ExportIcon />}
                      onClick={() => setExportDialogOpen(true)}
                      disabled={loading || !transactions || transactions.length === 0}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      Export Data
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Dashboard Metrics Cards */}
          <Grid container spacing={3} style={{ marginBottom: '32px' }}>
            <Grid item xs={12} sm={6} md={3}>
              <Slide direction="up" in timeout={1000}>
                <Card className={`${classes.metricCard} ${classes.metricCardGreen}`}>
                  <CardContent style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? (
                      <Skeleton variant="circle" width={64} height={64} className={classes.skeletonDark} style={{ margin: '0 auto 16px' }} />
                    ) : (
                      <AttachMoneyIcon style={{ fontSize: 48, color: '#22c55e', marginBottom: '16px' }} />
                    )}
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Total Revenue
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width="80%" height={40} className={classes.skeletonDark} style={{ margin: '0 auto' }} />
                    ) : (
                      <Typography variant="h4" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                        ₱{parseFloat(dashboardMetrics.totalRevenue).toLocaleString('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}
                      </Typography>
                    )}
                    <Typography style={{ color: '#a0aec0', marginTop: '8px', fontSize: '0.875rem' }}>
                      Avg: ₱{parseFloat(dashboardMetrics.avgBookingValue).toLocaleString('en-US', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })} per booking
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Slide direction="up" in timeout={1200}>
                <Card className={`${classes.metricCard} ${classes.metricCardBlue}`}>
                  <CardContent style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? (
                      <Skeleton variant="circle" width={64} height={64} className={classes.skeletonDark} style={{ margin: '0 auto 16px' }} />
                    ) : (
                      <EventAvailableIcon style={{ fontSize: 48, color: '#06b6d4', marginBottom: '16px' }} />
                    )}
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Today's Bookings
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width="60%" height={40} className={classes.skeletonDark} style={{ margin: '0 auto' }} />
                    ) : (
                      <Typography variant="h4" style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                        {dashboardMetrics.todayBookings}
                      </Typography>
                    )}
                    <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '8px' }}>
                      <TrendingUpIcon style={{ fontSize: 16, color: dashboardMetrics.weeklyGrowth >= 0 ? '#22c55e' : '#ef4444', marginRight: '4px' }} />
                      <Typography style={{ 
                        color: dashboardMetrics.weeklyGrowth >= 0 ? '#22c55e' : '#ef4444', 
                        fontSize: '0.875rem',
                        fontWeight: 'bold'
                      }}>
                        {dashboardMetrics.weeklyGrowth >= 0 ? '+' : ''}{dashboardMetrics.weeklyGrowth}% this week
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Slide direction="up" in timeout={1400}>
                <Card className={`${classes.metricCard} ${classes.metricCardPurple}`}>
                  <CardContent style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? (
                      <Skeleton variant="circle" width={64} height={64} className={classes.skeletonDark} style={{ margin: '0 auto 16px' }} />
                    ) : (
                      <GroupIcon style={{ fontSize: 48, color: '#8b5cf6', marginBottom: '16px' }} />
                    )}
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Active Customers
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width="60%" height={40} className={classes.skeletonDark} style={{ margin: '0 auto' }} />
                    ) : (
                      <Typography variant="h4" style={{ color: '#8b5cf6', fontWeight: 'bold' }}>
                        {dashboardMetrics.uniqueCustomers}
                      </Typography>
                    )}
                    <Typography style={{ color: '#a0aec0', marginTop: '8px', fontSize: '0.875rem' }}>
                      Unique customers served
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Slide direction="up" in timeout={1600}>
                <Card className={`${classes.metricCard} ${classes.metricCardOrange}`}>
                  <CardContent style={{ textAlign: 'center', padding: '24px' }}>
                    {loading ? (
                      <Skeleton variant="circle" width={64} height={64} className={classes.skeletonDark} style={{ margin: '0 auto 16px' }} />
                    ) : (
                      <SportsIcon style={{ fontSize: 48, color: '#f59e0b', marginBottom: '16px' }} />
                    )}
                    <Typography variant="h6" gutterBottom style={{ color: '#e2e8f0' }}>
                      Active Courts
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width="60%" height={40} className={classes.skeletonDark} style={{ margin: '0 auto' }} />
                    ) : (
                      <Typography variant="h4" style={{ color: '#f59e0b', fontWeight: 'bold' }}>
                        {dashboardMetrics.uniqueCourts}
                      </Typography>
                    )}
                    <Typography style={{ color: '#a0aec0', marginTop: '8px', fontSize: '0.875rem' }}>
                      Courts with bookings
                    </Typography>
                  </CardContent>
                </Card>
              </Slide>
            </Grid>
          </Grid>

          {/* Recent Transactions Section */}
          <Paper className={classes.styledPaper} style={{ marginBottom: '32px' }}>
            <Box style={{ padding: '24px', borderBottom: '1px solid #2d3748' }}>
              <Grid container alignItems="center" justify="space-between">
                <Grid item>
                  <Typography variant="h5" style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                    Recent Transactions
                  </Typography>
                  <Typography variant="body2" style={{ color: '#a0aec0', marginTop: '4px' }}>
                    Latest booking activities and payments
                  </Typography>
                </Grid>
                <Grid item>
                  <Chip 
                    label={`${transactions.length} Total Transactions`}
                    style={{
                      backgroundColor: 'rgba(6, 182, 212, 0.1)',
                      color: '#06b6d4',
                      fontWeight: 'bold'
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
            
            <Slide direction="up" in timeout={1800}>
              <TableContainer>
                {loading ? (
                  <Box style={{ padding: '32px' }}>
                    {[...Array(5)].map((_, index) => (
                      <Box key={index} style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'center' }}>
                        <Skeleton variant="circle" width={40} height={40} className={classes.skeletonDark} />
                        <Skeleton variant="text" width="20%" height={30} className={classes.skeletonDark} />
                        <Skeleton variant="text" width="25%" height={30} className={classes.skeletonDark} />
                        <Skeleton variant="text" width="15%" height={30} className={classes.skeletonDark} />
                        <Skeleton variant="text" width="15%" height={30} className={classes.skeletonDark} />
                        <Skeleton variant="rect" width="10%" height={25} className={classes.skeletonDark} />
                        <Skeleton variant="rect" width="10%" height={30} className={classes.skeletonDark} />
                      </Box>
                    ))}
                  </Box>
                ) : error ? (
                  <Box style={{ padding: '48px', textAlign: 'center' }}>
                    <AssessmentIcon style={{ fontSize: 60, color: '#ef4444', marginBottom: '16px' }} />
                    <Typography variant="h6" style={{ color: '#ef4444', marginBottom: '16px' }}>
                      {error}
                    </Typography>
                    <Button className={classes.styledButton} onClick={fetchTransactions}>
                      Try Again
                    </Button>
                  </Box>
                ) : !transactions || transactions.length === 0 ? (
                  <Box style={{ padding: '48px', textAlign: 'center' }}>
                    <ScheduleIcon style={{ fontSize: 60, color: '#4a5568', marginBottom: '16px' }} />
                    <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: '8px' }}>
                      No transactions found
                    </Typography>
                    <Typography variant="body2" style={{ color: '#a0aec0' }}>
                      Transactions will appear here once bookings are made
                    </Typography>
                  </Box>
                ) : (
                  <Table style={{ minWidth: 650 }}>
                    <TableHead className={classes.tableHead}>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Booking ID</TableCell>
                        <TableCell>Court Name</TableCell>
                        <TableCell align="right">Date</TableCell>
                        <TableCell align="right">Time</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Array.isArray(transactions) && transactions.slice(0, 5).map((transaction, index) => {
                        const customerName = getProperty(transaction, ['customerName', 'customer_name', 'userName', 'user_name']) || 'Unknown Customer';
                        
                        return (
                          <Fade in timeout={300 + index * 100} key={getProperty(transaction, ['id', '_id']) || index}>
                            <TableRow className={classes.tableRow}>
                              <TableCell>
                                <Box style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                  <Avatar style={{ 
                                    backgroundColor: '#06b6d4', 
                                    width: 32, 
                                    height: 32,
                                    fontSize: '0.875rem'
                                  }}>
                                    {customerName.charAt(0).toUpperCase()}
                                  </Avatar>
                                  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                    {customerName}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell component="th" scope="row">
                                <Chip 
                                  label={getProperty(transaction, ['bookingId', 'booking_id', 'bookingRef', 'booking_ref'])}
                                  size="small"
                                  style={{
                                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                    color: '#06b6d4',
                                    fontWeight: 'bold'
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                                  {getProperty(transaction, ['courtName', 'court_name'])}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {formatDate(getProperty(transaction, ['reservationDate', 'reservation_date']))}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {`${convertTo12Hour(getProperty(transaction, ['timeFrom', 'time_from']))} - ${convertTo12Hour(getProperty(transaction, ['timeTo', 'time_to']))}`}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body1" style={{ color: '#22c55e', fontWeight: 'bold' }}>
                                  ₱{parseFloat(transaction.total_amount || transaction.totalAmount || 0).toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip 
                                  label={getProperty(transaction, ['status', 'payment_status']) || 'Completed'} 
                                  size="small"
                                  style={{
                                    ...(((getProperty(transaction, ['status', 'payment_status']) || '').toLowerCase() === 'pending') && {
                                      backgroundColor: 'rgba(245, 158, 11, 0.1)',
                                      color: '#f59e0b'
                                    }),
                                    ...(((getProperty(transaction, ['status', 'payment_status']) || '').toLowerCase() === 'rejected') && {
                                      backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                      color: '#ef4444'
                                    }),
                                    ...(!['pending', 'rejected'].includes((getProperty(transaction, ['status', 'payment_status']) || '').toLowerCase()) && {
                                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                      color: '#22c55e'
                                    })
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title="View Financial Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewFinancialDetails(transaction)}
                                    style={{
                                      color: '#06b6d4',
                                    }}
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          </Fade>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TableContainer>
            </Slide>
          </Paper>
        </Box>
      </Fade>

      {/* QR Code Modal */}
      <Dialog
        open={openQRModal}
        onClose={() => setOpenQRModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          className: classes.dialogPaper
        }}
      >
        <DialogTitle style={{ 
          borderBottom: '1px solid #2d3748',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <QrCodeIcon style={{ color: '#06b6d4' }} />
          <Box>
            <Typography variant="h6" style={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Court Booking App QR Code
            </Typography>
            <Typography variant="body2" style={{ color: '#a0aec0' }}>
              Share this QR code to promote your booking app
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent style={{ padding: '32px', textAlign: 'center' }}>
          <Box style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: '24px'
          }}>
            {/* QR Code Display */}
            <Box style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}>
              <QRCodeDisplay qrImagePath="/static/rentacourt_user.png" size={250} />
            </Box>
            
            {/* App Information */}
            <Box style={{ textAlign: 'center' }}>
              <Typography variant="h6" style={{ color: '#e2e8f0', marginBottom: '8px' }}>
                Download Our Court Booking App
              </Typography>
              <Typography variant="body2" style={{ color: '#a0aec0', marginBottom: '16px' }}>
                Scan this QR code to download the app or visit our website
              </Typography>
              <Typography variant="body2" style={{ 
                color: '#06b6d4', 
                fontFamily: 'monospace',
                padding: '8px 16px',
                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                borderRadius: '6px',
                display: 'inline-block'
              }}>
                https://renta-court.pages.dev
              </Typography>
            </Box>

            {/* Instructions */}
            <Box style={{ 
              backgroundColor: '#1e293b', 
              padding: '16px', 
              borderRadius: '8px',
              width: '100%'
            }}>
              <Typography variant="subtitle2" style={{ color: '#06b6d4', marginBottom: '8px' }}>
                How to use this QR Code:
              </Typography>
              <Typography variant="body2" style={{ color: '#a0aec0', lineHeight: 1.6 }}>
                • Print and display at your courts for customers to scan<br/>
                • Share on social media to promote your business<br/>
                • Include in marketing materials and flyers<br/>
                • Email to existing customers
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        
        <DialogActions style={{ 
          padding: '24px', 
          borderTop: '1px solid #2d3748',
          gap: '12px'
        }}>
          <Button
            onClick={handleShareQR}
            startIcon={<ShareIcon />}
            style={{
              color: '#06b6d4',
              border: '1px solid #06b6d4'
            }}
          >
            Share
          </Button>
          <Button
            onClick={handlePrintQR}
            startIcon={<PrintIcon />}
            className={classes.styledButton}
          >
            Print QR Code
          </Button>
          <Button 
            onClick={() => setOpenQRModal(false)}
            style={{ color: '#a0aec0' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Financial Details Modal */}
      <Dialog
        open={openFinancialModal}
        onClose={() => setOpenFinancialModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: classes.dialogPaper
        }}
      >
        {selectedTransaction && (
          <>
            <DialogTitle style={{ 
              borderBottom: '1px solid #2d3748',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <MoneyIcon style={{ color: '#06b6d4' }} />
              Financial Details: {getProperty(selectedTransaction, ['bookingId', 'booking_id', 'bookingRef'])}
            </DialogTitle>
            <DialogContent dividers style={{ padding: '32px' }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper className={classes.styledPaper} style={{ padding: '24px', height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      Booking Information
                    </Typography>
                    
                    <Box style={{ marginBottom: '16px' }}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Typography><strong>Court Name:</strong></Typography>
                        <Typography>
                          {getProperty(selectedTransaction, ['courtName', 'court_name'])}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Typography><strong>Date:</strong></Typography>
                        <Typography>
                          {formatDate(getProperty(selectedTransaction, ['reservationDate', 'reservation_date']))}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Typography><strong>Time:</strong></Typography>
                        <Typography>
                          {`${convertTo12Hour(getProperty(selectedTransaction, ['timeFrom', 'time_from']))} - 
                          ${convertTo12Hour(getProperty(selectedTransaction, ['timeTo', 'time_to']))}`}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Typography><strong>Duration:</strong></Typography>
                        <Typography>
                          {getProperty(selectedTransaction, ['totalHours', 'total_hours'])} hours
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Typography><strong>Status:</strong></Typography>
                        <Chip 
                          label={getProperty(selectedTransaction, ['status', 'payment_status']) || 'Completed'} 
                          size="small"
                          style={{
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                            color: '#22c55e'
                          }}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Paper className={classes.styledPaper} style={{ padding: '24px', height: '100%' }}>
                    <Typography variant="h6" gutterBottom style={{ color: '#06b6d4', fontWeight: 'bold' }}>
                      Payment Breakdown
                    </Typography>
                    
                    <Box style={{ marginBottom: '16px' }}>
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Typography><strong>Base Amount:</strong></Typography>
                        <Typography>
                          ₱{parseFloat(getProperty(selectedTransaction, ['baseAmount', 'base_amount']) || 0).toLocaleString()}
                        </Typography>
                      </Box>
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Typography><strong>Additional Services:</strong></Typography>
                        <Typography>
                          ₱{parseFloat(getProperty(selectedTransaction, ['additionalAmount', 'additional_amount']) || 0).toLocaleString()}
                        </Typography>
                      </Box>
                      
                      <Divider style={{ margin: '16px 0', borderColor: '#2d3748' }} />
                      
                      <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <Typography><strong>Total Amount:</strong></Typography>
                        <Typography style={{ fontWeight: 'bold', color: '#22c55e' }}>
                          ₱{parseFloat(getProperty(selectedTransaction, ['totalAmount', 'total_amount']) || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions style={{ padding: '24px', borderTop: '1px solid #2d3748' }}>
              <Button className={classes.styledButton} onClick={() => setOpenFinancialModal(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      
      <ExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        currentTransactions={transactions}
      />
    </Box>
  );
}